/**
 * src/state/types/save.ts — root GameState shape + save-format version +
 * sentinel constants + entity-id allocator + command-rejection ledger.
 * Split from monolithic `types.ts` at MFix33.3 (2026-06-01).
 * Move-not-modify: contents preserved verbatim.
 */

import type { RngState } from '../rng';
import type { BalanceData } from './config';
import type {
  AiBehavioursData,
  AiTargetClaim,
  Empire,
  EmpireId,
  RaceDef,
  TeamId,
  TechDef,
} from './empires';
import type { FogOfWarSnapshot, VisibilityRecord } from './fow';
import type {
  EnvironmentEntity,
  HabitabilityData,
  Planet,
  PlanetId,
  StarSystem,
  SystemId,
  WarpGateLink,
  WarpGateLinkId,
} from './galaxy';
import type {
  AuraDef,
  ShipInstance,
  ShipInstanceId,
  ShipTemplate,
} from './ships';
import type { Structure, StructuresData } from './structures';
import type {
  BeamFire,
  BombardmentHitEvent,
  DamageEvent,
  PendingShot,
  Projectile,
  ProjectileId,
  WeaponDef,
  WeaponFireEvent,
  WeaponMechanics,
} from './weapons';

export type EntityId = number;

/**
 * v4M1.3 — a single command-rejection record. Pushed by `applyXyz`
 * validators when an issued command fails validation, drained by the
 * alert-feed each tick. Transient — never serialised, cleared at the
 * start of each `applyCommands` batch.
 *
 * `cmdKind` mirrors the discriminator on `Command` (e.g. `'jumpShips'`,
 * `'buildShip'`). `reason` is a player-facing string suitable for
 * direct surfacing as a toast message. Keep reasons short (one line),
 * specific (mention the constraint that failed), and constructive
 * (the player should know what they could change to make it succeed).
 *
 * `empireId` identifies the empire that issued the command; the
 * alert-feed filters to the player's empire so AI-issued rejections
 * (which should not happen in practice — AI logic validates before
 * submission — but exist as a belt-and-braces case) do not surface as
 * player toasts. Optional `empireId` because some appliers reject
 * before resolving an empire (e.g. lookup fails); in that case the
 * alert-feed treats the entry as "not for the player".
 */
export interface CommandRejection {
  cmdKind: string;
  reason: string;
  empireId?: EmpireId;
}

export interface GameState {
  /** Save-format version. Increment on any breaking schema change. */
  version: number;
  /** Monotonic tick counter; sim runs at 10 ticks/sec. */
  tick: number;
  /** Seeded RNG - every mutation that needs randomness advances this. */
  rng: RngState;
  /** Entity-id generator; next entity gets this value, then it increments. */
  nextEntityId: EntityId;
  empires: Map<EmpireId, Empire>;
  planets: Map<PlanetId, Planet>;
  systems: Map<SystemId, StarSystem>;
  /**
   * v3M18 — explicit player-authored Warp Gate connections. Empty at scenario
   * init; populated by `applyConnectWarpGate` and drained by
   * `applyDisconnectWarpGate` / on-destroy handling. Each entry pairs two
   * Warp Gate ShipInstances into a virtual hyperlane; see WarpGateLink for
   * lifecycle. Strictly per-empire (every link's gateA + gateB share an
   * empire); we don't keep an empire-keyed index because the Map is small
   * and direct iteration is cheap.
   */
  warpGateLinks: Map<WarpGateLinkId, WarpGateLink>;
  ships: Map<ShipInstanceId, ShipInstance>;
  /**
   * v3M28 — persistent missile entities currently in flight. Inserted
   * by `enqueueMissile` at fire-time (from the combat dispatch path
   * for any `subType === 'Missile'` weapon), walked by
   * `advanceMissiles` per tick, removed on arrival / target-gone /
   * target-jumped per `MISSILES.md §7`. Empty Map at scenario init;
   * empty Map after a battle settles. Each entry carries its own
   * Bezier trajectory + accuracy outcome so per-tick advancement is
   * a pure function of the entity + game tick. See `MISSILES.md` for
   * the authoritative design.
   *
   * v4M9.1b — renamed from `missiles` to `projectiles` ahead of the
   * v4M9.2-.4 projectile unification (Torpedo / Pulse / Burst migrate
   * onto this collection). Field still carries `Missile` entries until
   * the per-family migrations land; the rename is name-only at this
   * checkpoint to keep call sites stable through .1b. SAVE_FORMAT_VERSION
   * bumped 20 → 21 to fail-fast on pre-rename saves.
   *
   * v4M9.2 — entries gained a `family: ProjectileFamily` discriminant;
   * `Missile` renamed to `Projectile` (Missile retained as type alias).
   * Torpedo joins the family enum at this checkpoint; Pulse / Burst
   * follow in .3-.4. SAVE_FORMAT_VERSION bumped 21 → 22 to fail-fast
   * on pre-discriminant saves.
   */
  projectiles: Map<ProjectileId, Projectile>;
  /**
   * All planet-side structures (STRUCTURES §2). Each is referenced from its
   * owning planet's `structureIds: EntityId[]`. Space-based structures are
   * stored in `ships` per the STRUCTURES §3 "stationary-ships" classification,
   * not here — this map is planet-side only.
   */
  structures: Map<EntityId, Structure>;
  /**
   * Tactical-map environmental entities (M14). Asteroid fields and gas
   * giants are sited per system at scenario gen; Constructor-built
   * resource extractors (Asteroid Mining Outpost, Gas Giant Refinery)
   * require a matching entity under the Constructor's current position
   * at build time. Read-only after scenario init in v1.
   */
  environment: Map<EntityId, EnvironmentEntity>;
  /** Static ship templates loaded from data/ships.json. Never mutated post-load. */
  shipTemplates: ReadonlyMap<string, ShipTemplate>;
  /**
   * MFix8.5 — Aura definitions loaded from the `Auras` sheet on
   * Ships.xlsx, keyed by aura name. Emitter ships reference an aura
   * via `ShipTemplate.aura`; the aura helper looks up this map at
   * runtime to apply per-ship bonuses. Static post-load like the other
   * data catalogues; dropped from saves and re-attached on load.
   *
   * Optional rather than required so existing GameState literals
   * across tests don't need a fixture sweep — `auraBonusesForShip`
   * defensively returns the no-aura default when `state.auras` is
   * undefined or empty. Mirrors the `chassisLevel?` precedent from
   * v3M2.2.
   */
  auras?: ReadonlyMap<string, AuraDef>;
  /**
   * Static weapon catalogue loaded from data/ships.json, keyed by
   * `weapon.name` (e.g. 'Pulse Laser Mk 1'). Ship loadouts reference weapons
   * generically by *slot* (e.g. 'Energy Pulse Weapon'); the combat pass
   * resolves the slot to a concrete weapon in this map, picking the highest
   * Mk the owning empire has researched (falling back to the Mk 1 variant,
   * which has researchCost 0 — always available). Never mutated post-load.
   */
  weapons: ReadonlyMap<string, WeaponDef>;
  /**
   * Name-keyed tech catalogue from Tech_Tree.xlsx (M12). Keyed by `techName`.
   * Used by `applySetResearchTarget` to validate targets, by
   * `advance-research.ts` to look up researchCost during the drain pass,
   * and (in M12.2+) by effect helpers that read category + level for
   * Hull/Armor/Shield/Hyperdrive progressions. Never mutated post-load.
   */
  techs: ReadonlyMap<string, TechDef>;
  /**
   * MFix3.2 — Habitability catalogue from `docs/Habitability.xlsx`. Drives
   * `habitabilityFor(planet, race, researchedTechs)` and per-band population
   * dynamics in `advancePopulationAndEconomy`. Static post-load like the
   * other data catalogues; dropped from saves and re-attached on load.
   */
  habitability: HabitabilityData;
  /**
   * v3M7.1 — AI behaviours catalogue from `docs/AI_Behaviours.xlsx`. Drives
   * `getAiConfig(race, difficulty, behaviour)` in `balance-ai.ts`. Static
   * post-load; dropped from saves and re-attached on load.
   */
  aiBehaviours: AiBehavioursData;
  /**
   * v3M21.5 — race catalogue from Ships.xlsx Races sheet. Carries the
   * per-race `diplomaticAggressionMultiplier` consumed by the AI
   * dispatch loop. Optional to avoid a ~30-fixture sweep — the
   * runtime helpers default to multiplier `1.0` when absent (matches
   * the MFix8.5 `auras?` pattern). Static post-load; re-attached
   * from `LoadedGameData` on load like the other catalogues.
   */
  races?: ReadonlyArray<RaceDef>;
  /**
   * v3M14a — Structure tunables catalogue from `docs/Structures.xlsx`.
   * Per-kind build cost / build ticks / hull / max-level / race
   * overrides / unique flag / upkeep / economy multiplier, plus the
   * empire-wide flat caps and the Embassy diplomacy curve. Static
   * post-load; dropped from saves and re-attached on load like the
   * other catalogues.
   *
   * Named `structuresData` to avoid colliding with the existing
   * `state.structures: Map<EntityId, Structure>` field which holds
   * per-planet structure instances. v3M14a wires this through but
   * consumer migration (replacing the file-level constants in
   * `balance-structures.ts` with state-driven lookups) lands in
   * v3M14b.
   */
  structuresData: StructuresData;
  /**
   * v3M10.1 — cross-cutting balance constants catalogue. Static post-
   * load; dropped from saves and re-attached on load from
   * `LoadedGameData` like the other domain catalogues. See
   * `BalanceData` for the field map.
   */
  balanceData: BalanceData;
  /**
   * Per-team visibility: TeamId -> (ShipInstanceId -> VisibilityRecord). One
   * row per *target* the team currently sees or remembers within its grace
   * window. Written by the detection pass in advanceTick; read by renderers
   * (fog of war) and combat targeting.
   *
   * In v1, TeamId === EmpireId, so every empire gets its own map initialised
ion", so a
   * fresh game starts fully fogged except for an empire's own ships (which
   * are never in teamVision and always render).
   */
  teamVision: Map<TeamId, Map<ShipInstanceId, VisibilityRecord>>;
  /**
   * v4M10.1 — Per-empire Fog-of-War snapshots per `docs/FOW.md §3.1`.
   * Outer key: viewer empire id. Inner: per-system snapshot of last-
   * seen state when the empire most recently held vision.
   *
   * Refreshed every tick by the FoW capture pass at the end of
   * `advanceTick` for every (viewer, scouted system) pair (v4M10.1
   * strict-parity: capture for every scouted system; v4M10.2 narrows
   * to Seen-only systems once the layer resolver lands). Preserved
   * verbatim once vision is lost (the v4M10.2 helper handles the
   * Seen → Fog of War transition; v4M10.1 just maintains the snapshot
   * data).
   *
   * Snapshots include planets + structures at last sight. NOT included:
   * ships (handled at finer granularity by `teamVision`), in-flight
   * orders, transient sim state. Terrain features are state-stable
   * post-scenario-init so they're resolved live from `state.environment`
   * rather than snapshotted.
   */
  fogOfWarSnapshots: Map<EmpireId, Map<SystemId, FogOfWarSnapshot>>;
  /**
   * v4M8.7 — per-team AI target claims for the team-aware coordinated
   * combat dispatch (AI.md §5.8). When one teammate AI dispatches a
   * combat task force to a system, it pushes a claim here; other
   * teammates' pickers subtract `teammatePenalty × claim-count` from
   * the candidate target score, splitting the team's attention
   * across different targets.
   *
   * **Runtime-only — NOT serialised** by design. Claims are ephemeral
   * (TTL ~200 ticks per `claimTtlTicks`) and the AI re-derives within
   * 20 s of play after a load, so the fixture-sweep cost of saving
   * them isn't worth the marginal value. `serialize.ts` and
   * `deserialize.ts` skip this field; loaded games start with an
   * empty Map. Optional (`?`) so call sites can `?? new Map()` it.
   */
  teamClaims?: Map<TeamId, AiTargetClaim[]>;
  /**
   * v3M19 — per-tick combat-event buffer for the floating-damage-numbers
   * VFX dispatch. Cleared at the start of every `advanceCombat` pass and
   * populated as the pass resolves volleys; drained by the render layer
   * between sim ticks. Transient — excluded from the save serialise body
   * and re-attached as `[]` on deserialise. See `DamageEvent` for the
   * per-event shape and the dispatch semantics.
   */
  combatEvents: DamageEvent[];
  /**
   * v4M1.3 — per-tick command-rejection buffer for the alert-feed surface.
   * Cleared at the start of every `applyCommands` batch and populated as
   * individual `applyXyz` validators reject player-issued commands;
   * drained by the alert-feed layer between sim ticks (one toast per
   * rejection). Transient — excluded from the save serialise body and
   * re-attached as `[]` on deserialise. AI-driven dispatch attempts
   * (e.g. `advance-ai.ts` calls to `applyCommand`) deliberately suppress
   * push via the `suppressRejectionFeedback` flag on `applyCommands`
   * opts — only player-facing rejections surface as toasts.
   *
   * v4M5 maintenance note: when v4M5.2 lands the new range cap + fuel
   * formula + Jump Area mechanics, `applyJumpShips` and
   * `applyMoveAndJumpShips` rejection strings need updating (new
   * "out of range" failure mode; removed "no jump point selected"
   * mode). `applyBuildSpaceStructure` warp-gate branch likewise gets
   * a cost-update pass.
   */
  commandRejections: CommandRejection[];
  /**
   * MFix11.5 — per-tick weapon-fire-event buffer for per-weapon-slot
   * tracer scheduling. Cleared at the start of every `advanceTick`
   * before the combat pass repopulates; drained by `vfx.ts:advanceVfx`
   * each frame to schedule a TracerVfx per fired slot. Transient —
   * excluded from the save serialise body and re-attached as `[]` on
   * deserialise. See `WeaponFireEvent` for the per-event shape.
   *
   * Replaces the v2M7.4c approximation where each ship-fire-event
   * scheduled a single tracer with the FIRST weapon's subType — a
   * mixed-loadout ship (e.g. Cruiser carrying Railgun + 3× Burst)
   * previously rendered only the Railgun tracer regardless of which
   * slots actually fired. Per-slot events let the renderer schedule
   * one tracer per fire, attributed to the correct weapon subType.
   */
  weaponFireEvents: WeaponFireEvent[];
  /**
   * MFix42.5b (2026-06-01) — per-tick bombardment-hit-event buffer for
   * the planet-impact destruction-style explosion VFX. Cleared at the
   * start of every `advanceTick` alongside `combatEvents` /
   * `weaponFireEvents` and populated as `advance-bombardment.ts`
   * resolves each weapon-slot volley; drained by `vfx.ts:advanceVfx`
   * to schedule explosion VFX anchored at the planet's in-system
   * position. Transient — excluded from the save serialise body and
   * re-attached as `[]` on deserialise. See `BombardmentHitEvent`
   * (weapons.ts) for the per-event shape.
   *
   * Optional rather than required to avoid a sweep of all test fixtures
   * (matches the `gracefulDespawnIds` pattern). Production paths
   * (`deserialize.ts` load + `advance-combat.ts` per-tick reset) always
   * initialise to `[]`, so consumers can rely on it post-load. Test
   * fixtures get undefined → the drain in `vfx.ts` skips harmlessly.
   */
  bombardmentEvents?: BombardmentHitEvent[];
  /**
   * MFix12.4 — deferred-instant-hit shot queue for Railgun's
   * inter-slot stagger. Populated by `fireSalvo` when a multi-instance
   * Railgun fires with `slotStaggerTicks > 0`; consumed by
   * `advancePendingShots` per tick. Transient — excluded from the save
   * body, re-attached as an empty array on load. In-flight pending
   * shots from a mid-combat save are lost; same trade-off as
   * `combatEvents`.
   *
   * Optional to avoid a ~30-fixture sweep — the producers lazy-init
   * via `state.pendingShots ??= []` and consumers guard the iterator.
   * `scenario.ts` + `deserialize.ts` set this to `[]` explicitly, so
   * any state built through the canonical seed path always has the
   * field populated; test fixtures that hand-build minimal states
   * keep the field absent and the lazy-init handles it.
   */
  pendingShots?: PendingShot[];
  /**
   * MFix13 — set of ShipInstanceIds whose despawn this tick should be
   * treated as a "graceful" exit (no destruction VFX, no `ship:destroyed`
   * audio cue, no "ship lost" alert). Populated by code paths that
   * dispose of ships for non-combat reasons: carrier squadron recall,
   * carrier-jump auto-recall of deployed squadrons. The vfx + audio
   * diff layers check this set when diffing `lastShipSnapshot` against
   * the current `state.ships` to decide whether a missing ship is a
   * destruction or a graceful despawn.
   *
   * Transient — excluded from the save body, cleared at the start of
   * each `advanceTick`. Optional to avoid a fixture sweep; producers
   * lazy-init via `(state.gracefulDespawnIds ??= new Set()).add(id)`.
   */
  gracefulDespawnIds?: Set<ShipInstanceId>;
  /**
   * v4M9.1a — per-family weapon-mechanics catalogue loaded from
   * `docs/weapon_mechanics.xlsx` → `src/data/weapon-mechanics.json`.
   * One sheet per weapon subType, name-keyed within each family.
   * Read at fire time to drive flight speed (projectile families),
   * sustain duration + damage-per-tick (Beam), visual-sustain
   * (Railgun), etc. Authoritative spec at `docs/PROJECTILES.md`.
   *
   * Static catalogue loaded once at scenario init; never modified
   * post-load. Same lifecycle as `state.weapons` / `state.techs` /
   * `state.shipTemplates`.
   */
  weaponMechanics: WeaponMechanics;
  /**
   * v4M9.1a — in-flight Beam sustain entities. Populated by combat
   * dispatch when a Beam slot fires (v4M9.5); ticked by
   * `advance-beams.ts` (v4M9.5) until sustain expiry or target loss.
   * Empty in v4M9.1a — schema field reserved for the .5 implementation.
   * Transient — excluded from the save body in v4M9.1a; deserialise
   * re-attaches as an empty Map.
   */
  beams: Map<number, BeamFire>;
  /** Primary local player's empire id. Stable across saves. */
  playerEmpireId: EmpireId;
  /**
   * v3M12 — game-end outcome. `'in-progress'` for an active game;
   * `'victory'` when every non-player empire has been eliminated
   * (zero planets owned); `'defeat'` when the player's empire has
   * zero planets (incl. via manual Concede). Set by
   * `advance-game-end.ts` after combat / bombardment passes have
   * settled, or by `applyConcede`. While non-`'in-progress'`,
   * `advanceTick` is a no-op — the world freezes mid-frame at the
   * outcome moment so the end-game overlay can mount + animate.
   * Saved/loaded transparently; older saves without the field
   * default to `'in-progress'`.
   */
  gameOutcome: 'in-progress' | 'victory' | 'defeat';
}

// ---------------------------------------------------------------------------
// MFix33.3a (2026-06-01) — restored from pre-split types.ts.
// These 3 value exports were missed by the Python split script's
// EXPORT_TO_FILE map (parser caught block boundaries but the map keyed
// only the type/interface names that lived next to them, dropping the
// trailing const/function exports). Block content extracted verbatim
// from commit 23ef29e:src/state/types.ts lines 3144-3272.
// ---------------------------------------------------------------------------

/**
 * v4M5.3b1 bumped 16 → 17 when `ShipInstance` gained
 * `pendingJumpSystemId` + `pendingJumpArrivalAngle` for the
 * continuous-Jump-Area dispatch model per `HYPERSPACE.md §4`. v4M5.3b1
 * was additive (legacy `pendingJumpPointId` + `jumpTargetJumpPointId`
 * stayed on ShipInstance alongside the new fields). Pre-v17 saves
 * fail-fast at the deserialise version gate per the established v4M5
 * hard-break pattern.
 *
 * v4M5.3b3a bumped 17 → 18 when those legacy fields were retired
 * (`pendingJumpPointId` dropped entirely; `jumpTargetJumpPointId`
 * replaced by `jumpArrivalAngle: number | null`). `applyJumpShips` +
 * `applyMoveAndJumpShips` now consume `targetSystemId` + `arrivalAngle`
 * natively (the v4M5.3b2 `resolveJumpPointForCommand` adapter is gone).
 *
 * v4M5.3b3c bumped 18 → 19 when `state.jumpPoints: Map<JumpPointId, JumpPoint>`
 * + the `JumpPoint` interface + the `JumpPointId` type retired entirely.
 * `StarSystem.jumpPointIds: JumpPointId[]` collapsed to
 * `StarSystem.adjacentSystemIds: SystemId[]` — symmetric system-pair
 * adjacency now lives directly on the StarSystem record. Galaxy-view
 * hyperlanes and AI multi-hop dispatch walk this list directly.
 *
 * v4M4.e.1 bumps 19 → 20 alongside the Weapons Platform 3 → 6 subType
 * expansion. Three retiring chassis (`Energy Weapons Platform`,
 * `Kinetic Weapons Platform`, `Concussion Weapons Platform`) and six
 * incoming chassis (`Pulse / Beam / Railgun / Burst / Torpedo /
 * Missile Weapons Platform`). Hard save break — Alpha 0.6 saves with
 * built old-name platforms reject at deserialise. Also covers the
 * `SpaceStructureRow` field expansion (`hull` / `shields` /
 * `maintenanceCredits` / `maintenanceMinerals` / `maxLevelByRace` /
 * `weaponSubtypeEmitter`) lifted from `SPACE_STRUCTURE_SPECS` into
 * the xlsx-sourced `structures.spaceByKind` map.
 *
 * v4M9.1b bumps 20 → 21 when `state.missiles` was renamed to
 * `state.projectiles` ahead of the v4M9.2-.4 unification (Torpedo /
 * Pulse / Burst migrate onto the collection). Hard save break — pre-
 * rename saves fail-fast at the deserialise version gate. Same
 * "no migration" policy as every prior bump.
 *
 * v4M9.2 bumps 21 → 22 when each `Projectile` entry gained a
 * `family: ProjectileFamily` discriminant (`'Missile' | 'Torpedo'`).
 * Pre-discriminant entries have no `family` field and would surface
 * as `undefined` in the dispatch switch; hard break instead. Same
 * "no migration" policy as every prior bump.
 *
 * v4M9.3 bumps 22 → 23 when `'Pulse'` joined the `ProjectileFamily`
 * enum and Pulse weapons migrated from the instant-hit + TracerVfx
 * model to the persistent-projectile pipeline. Pre-v23 saves may
 * carry in-flight `weaponFireEvents` that expect Pulse to resolve
 * via the tracer path; the new dispatch routes Pulse subType through
 * `enqueuePulse` instead, so a partial mid-combat blob loaded under
 * v4M9.3 would drop those in-flight rounds. Hard break instead.
 *
 * v4M9.4 bumps 23 → 24 when `'Burst'` joined the `ProjectileFamily`
 * enum and Burst weapons migrated to the persistent-projectile pipeline
 * with multi-round stagger. Same rationale as the Pulse bump.
 *
 * v4M9.5 bumps 24 → 25 when `state.beams` became persistent (a
 * `BeamFire` map of in-flight sustained beam channels). Pre-v25 saves
 * have an empty `beams` field because the field was added as a
 * transient placeholder in v4M9.1a — loading them under v4M9.5 would
 * lose any in-flight beam sustain, equivalent to dropping in-flight
 * projectiles. Hard break instead.
 *
 * MFix13.3e bumps 25 → 26 when squadrons gained `isReturning?: boolean`
 * to mark a committed fly-back-to-carrier (manual recall + auto-recall
 * -at-low). Pre-v26 saves with a deployed squadron mid-flight would
 * lose the committed-return state and silently switch back to engage-
 * if-target. Hard break to keep behaviour deterministic across the
 * version transition.
 *
 * MFix19 bumps 26 → 27 when `BeamFire.slotIndex` was added so the
 * renderer can fan multiple beams from the same firer onto distinct
 * emission points. In-flight beams from pre-v27 saves default to
 * slotIndex 0 (single-channel render) — the visual would temporarily
 * collapse multiple channels to one line until the next fire-event,
 * which is acceptable on a save-resume.
 *
 * MFix29.1 bumps 27 → 28 when `Projectile.slotPerp` was added so the
 * pulse renderer can re-anchor visible spawn to the firer's current
 * position each frame (eliminates the "bolt fires from behind the
 * ship" artefact when firer moves). In-flight projectiles from
 * pre-v28 saves deserialise with `slotPerp = 0` — they render with
 * no slot offset on resume, which collapses fans to centerline until
 * the next fire-event.
 *
 * v4M8.1 bumps 28 → 29 for the AI behaviour restructure: Empire field
 * rename (`aiBehaviour: AiBehaviour` → `aiArchetype: ArchetypeId`), the
 * legacy six-value behaviour union dropped (no clean Balanced/Economist
 * mapping), and the `AiDifficulty` union restructured (Normal → Medium;
 * Brutal added). Existing saves carry legacy field name + dropped
 * values; rejecting up front via the version bump avoids ambiguous
 * hand-migration. AI.md §7 noted 15 → 16 as the design-lock-time bump;
 * the version line has incremented through many other milestones in
 * the meantime, so the live bump is 28 → 29.
 */
// v4M11.1 — bumped 30 → 31 for the per-empire in-system-fog discovered
// sets (discoveredPlanetIds + discoveredEnvironmentIds). Lenient back-
// compat on load: pre-v31 blobs reconstruct the discovered sets by
// walking each scouted system's entity list per `IN_SYSTEM_FOG.md §3`,
// faithfully preserving the pre-fog "entered a system = everything
// revealed" semantic. Alpha 0.6 saves carry over without intel loss.
//
// v4M13.4a — bumped 31 → 32 for the EnvironmentKind rename `'gas-giant'`
// → `'exotic-gas-cloud'` + the EnvironmentEntity.effects override field
// added at v4M13.4a. Lenient back-compat on load translates the old
// string per `deserialize.ts`.
//
// MFix42.2 (2026-06-01) — bumped 32 → 33 for the Missile Weapons Platform
// removal. Hard save break: pre-v33 saves with built Missile Weapons
// Platforms reject at the deserialise version gate. No back-compat
// translation — the chassis + structure kind don't exist in the
// runtime catalogue anymore, so any persisted reference becomes
// undefined. Same "no migration" policy as every prior structure-kind
// removal.
//
// MFix42.8a (2026-06-02) — bumped 33 → 34 for the research progress
// retention + queue rework on Empire: `parkedResearchProgress` Map and
// `researchQueue` string[] added. Hard save break at the version gate
// (same as every prior bump); the empty-Map / empty-array defaults in
// `deserializeEmpire` are deserialiser-side robustness for the absence
// of those keys in the serialised shape — they do NOT bridge across
// the version-gate reject. Alpha 0.6 saves were already broken at
// MFix42.2 (the Missile Weapons Platform removal), so this bump
// imposes no incremental player-side cost.
export const SAVE_FORMAT_VERSION = 34;

/**
 * v3M21.1 — Reserved EmpireId for the synthetic neutral empire.
 *
 * Owns all populated-but-unaffiliated planets seeded by scenario-gen
 * (`DIPLOMACY.md §3`) plus their pre-built defensive structures. Has
 * race `'Neutral'` and AI behaviour `'Neutral'`; takes no AI actions,
 * does not research, does not build, does not dispatch. Iteration
 * sites that walk `state.empires` for AI dispatch / turn order /
 * scoreboard / victory checks must filter via
 * `if (empire.id === EMPIRE_NEUTRAL_ID) continue;`.
 *
 * Value: 0. The default `playerEmpireId: 0` placeholder set during
 * state init is overwritten before any consumer reads it (see
 * `scenario.ts` immediately after `makeEmpire(playerEmpire)` returns),
 * and `nextEntityId: 1` keeps fresh empire ids strictly above this
 * reserved value, so no allocation collision is possible.
 */
export const EMPIRE_NEUTRAL_ID: EmpireId = 0;

/** Allocate a new entity id, mutating nextEntityId. */
export function allocEntityId(state: GameState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return id;
}
